import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import { useGetAllowedTokensParameterGroupQuery } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import type { ParameterGroup } from 'Services/ProductManagement/Features/Parameter/types'
import ProductAvailability from 'Services/ProductManagement/Features/Product/Components/ProductAvailability/ProductAvailability'
import { ProductDetails } from 'Services/ProductManagement/Features/Product/Components/ProductDetails'
import { SELECTED_PRODUCT_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Hooks/useProductsWithParams'
import { ProductTabType } from 'Services/ProductManagement/Features/Product/productSlice'
import { PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY } from 'Services/ProductManagement/Features/Tariff/Pages/TariffProductListPage'
import useTypedSelector from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { PRODUCT_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface ProductDrawerProps {
  open: boolean
  handleClose: () => void
  handleProductDelete: () => void
  productGroupParameterGroup: ParameterGroup | null
  isProductGroupParameterGroupLoading: boolean
  customerGroupParameterGroup: ParameterGroup | null
  isCustomerGroupParameterGroupLoading: boolean
}

export function ProductDrawer({
  open,
  handleClose,
  handleProductDelete,
  productGroupParameterGroup,
  isProductGroupParameterGroupLoading,
  customerGroupParameterGroup,
  isCustomerGroupParameterGroupLoading
}: Readonly<ProductDrawerProps>) {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/products`
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const drawerTabFromParams =
    searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) ?? ProductTabType.DETAILS
  const { confirm } = useConfirm()
  const { singleProduct, singleLoading: singleProductLoading } = useTypedSelector(
    state => state.product
  )
  const [isAvailabilityEdited, setIsAvailabilityEdited] = useState(false)

  const previousLocation = usePreviousLocation(
    `${baseUrl}?${SELECTED_PRODUCT_PARAMETER_KEY}=${singleProduct?.id}`
  )

  const {
    data: allowedTokensParameterGroup = null,
    isLoading: allowedTokensParameterGroupLoading
  } = useGetAllowedTokensParameterGroupQuery(undefined, {
    skip: !open
  })

  const handleDrawerTabChange = (_event: React.SyntheticEvent, newValue: ProductTabType) => {
    if (
      isAvailabilityEdited &&
      searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) === ProductTabType.AVAILABILITY
    ) {
      confirm({
        title: t`Are you sure?`,
        message: t`All changes will be lost. Are you sure you want to cancel?`,
        handleConfirm: () => {
          searchParams.set(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
          setSearchParams(searchParams)
        }
      })
    } else {
      searchParams.set(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
      setSearchParams(searchParams)
    }
    setIsAvailabilityEdited(false)
  }

  const handleDrawerClose = () => {
    if (
      isAvailabilityEdited &&
      searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) === ProductTabType.AVAILABILITY
    ) {
      confirm({
        title: t`Are you sure?`,
        message: t`All changes will be lost. Are you sure you want to cancel?`,
        handleConfirm: () => {
          handleClose()
          searchParams.delete(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY)
          setSearchParams(searchParams)
        }
      })
    } else {
      handleClose()
      searchParams.delete(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY)
      setSearchParams(searchParams)
    }
    setIsAvailabilityEdited(false)
  }

  const isSingleProductLoading = singleProductLoading === 'pending'

  return (
    <DrawerRight
      open={open}
      onClose={handleDrawerClose}
      topBar={
        <Box>
          {singleProduct && (
            <>
              <DeleteButton
                sx={{ mr: 1 }}
                size="small"
                onClick={() =>
                  confirm({
                    title: t`Are you sure?`,
                    message: t`Are you sure you want to delete the product?`,
                    handleConfirm: () => {
                      handleProductDelete()
                    }
                  })
                }
              >{t`Delete`}</DeleteButton>
              <CustomButton
                onClick={() => navigate(`${baseUrl}/${singleProduct.id}/edit`)}
                size="small"
                startIcon={<EditIcon />}
              >{t`Edit`}</CustomButton>
            </>
          )}
        </Box>
      }
    >
      <WithLoader loading={isSingleProductLoading}>
        {singleProduct && (
          <TabContext value={drawerTabFromParams}>
            <TabList onChange={handleDrawerTabChange} aria-label={t`Product tab list`}>
              <Tab label={t`Product details`} value={ProductTabType.DETAILS} />
              <Tab label={t`Availability`} value={ProductTabType.AVAILABILITY} />
            </TabList>
            <Divider />
            <TabPanel sx={{ p: 0 }} value={ProductTabType.DETAILS}>
              <ProductDetails
                product={singleProduct}
                productGroupParameterGroup={productGroupParameterGroup}
                isProductGroupParameterGroupLoading={isProductGroupParameterGroupLoading}
                customerGroupParameterGroup={customerGroupParameterGroup}
                isCustomerGroupParameterGroupLoading={isCustomerGroupParameterGroupLoading}
                allowedTokensParameterGroup={allowedTokensParameterGroup}
                isAllowedTokensParameterGroupLoading={allowedTokensParameterGroupLoading}
              />
            </TabPanel>
            <TabPanel sx={{ p: 0 }} value={ProductTabType.AVAILABILITY}>
              <ProductAvailability
                product={singleProduct}
                successBaseUrl={previousLocation}
                handleClose={handleDrawerClose}
                isEdited={isAvailabilityEdited}
                setIsEdited={setIsAvailabilityEdited}
              />
            </TabPanel>
          </TabContext>
        )}
      </WithLoader>
    </DrawerRight>
  )
}
